// <copyright file="purchase-order-batch-dialog.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { html, TemplateResult } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { LangPurchaseOrderDetailView } from '../../../localization/scripts/purchase-orders/purchase-order-detail-view-lang';
import { LitBaseModal } from '../../../webmodule-common/other/ui/modal/modal-factory-lit';
import { PurchaseOrderBatch } from '../../api/dealer-api-interface-franchisee';
import { serverDateTimeToLocal } from '../../../webmodule-common/other/datetime-converter';

/**
 * Read-only view of a purchase order delivery batch (MRD, Task 230006). Mirrors the quote page's read-only
 * "View Group" dialog, but shows BOTH sides of the batch that the PO domain owns:
 *  - the <b>Requested</b> values the dealer authored on the quote (snapshotted at PO creation), and
 *  - the values <b>Confirmed by Supplier</b> — the V6-synced (<c>supplierSystem*</c>) columns filled by the
 *    order-status poller after the order is placed.
 *
 * Purely informational: no editing, a single Close button (same chrome as the quote read-only group dialog).
 */
export class PurchaseOrderBatchDialog extends LitBaseModal {
  private readonly batch: PurchaseOrderBatch;

  constructor(batch: PurchaseOrderBatch) {
    super();
    this.batch = batch;
  }

  /** Show the read-only batch details dialog. */
  public static async Execute(batch: PurchaseOrderBatch): Promise<void> {
    const dialog = new PurchaseOrderBatchDialog(batch);
    await dialog.showModal();
  }

  get isFooterVisible(): boolean {
    return true;
  }

  title(): string | TemplateResult {
    return LangPurchaseOrderDetailView.batchDetailsTitle();
  }

  bodyTemplate(): TemplateResult {
    const b = this.batch;

    // A value or an em-dash placeholder when the field is empty.
    const value = (v: string | null | undefined): string =>
      isEmptyOrSpace(v ?? undefined) ? LangPurchaseOrderDetailView.batchNotProvided() : (v as string);
    // Delivery dates arrive as server date(-time) strings. Rendered in the viewer's locale and zone with
    // the same toLocaleDateString/toLocaleTimeString presentation the rest of the app uses for dates - the
    // RFC3339 helpers are input[type=date] value formatters and would show a raw "2026-07-28T14:30:00".
    const dateValue = (v: string | null | undefined): string =>
      isEmptyOrSpace(v ?? undefined)
        ? LangPurchaseOrderDetailView.batchNotProvided()
        : serverDateTimeToLocal(v as string).toLocaleDateString();
    const dateTimeValue = (v: string | null | undefined): string => {
      if (isEmptyOrSpace(v ?? undefined)) return LangPurchaseOrderDetailView.batchNotProvided();
      const dt = serverDateTimeToLocal(v as string);
      return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
    };

    const row = (label: string, display: string): TemplateResult => html`
      <div class="po-batch-row" style="display:flex; gap:0.75rem; padding:0.2rem 0;">
        <span class="po-batch-label" style="flex:0 0 9rem; font-weight:600;">${label}</span>
        <span class="po-batch-value" style="flex:1 1 auto;">${display}</span>
      </div>
    `;

    // Nothing has been synced from the supplier yet: show one clear placeholder rather than a wall of dashes.
    const hasConfirmed =
      !isEmptyOrSpace(b.supplierSystemDeliveryDate ?? undefined) ||
      !isEmptyOrSpace(b.supplierSystemCustomerRef ?? undefined) ||
      !isEmptyOrSpace(b.supplierSystemStatus ?? undefined) ||
      !isEmptyOrSpace(b.supplierSystemLastModifiedDate ?? undefined);

    const confirmedBody = hasConfirmed
      ? html`
          ${row(LangPurchaseOrderDetailView.batchDeliveryDate(), dateValue(b.supplierSystemDeliveryDate))}
          ${row(LangPurchaseOrderDetailView.batchCustomerRef(), value(b.supplierSystemCustomerRef))}
          ${row(LangPurchaseOrderDetailView.batchStatus(), value(b.supplierSystemStatus))}
          ${row(LangPurchaseOrderDetailView.batchLastUpdated(), dateTimeValue(b.supplierSystemLastModifiedDate))}
        `
      : html`<div
          class="po-batch-awaiting"
          style="color:var(--webmodule-color-neutral-500,#64748b); font-style:italic;"
        >
          ${LangPurchaseOrderDetailView.batchAwaitingSupplier()}
        </div>`;

    return html`
      <div class="po-batch-dialog">
        ${row(LangPurchaseOrderDetailView.batchDescription(), value(b.description))}
        <div class="po-batch-columns" style="display:flex; gap:2rem; flex-wrap:wrap; margin-top:0.75rem;">
          <section class="po-batch-section" style="flex:1 1 16rem; min-width:14rem;">
            <h5
              style="margin:0 0 0.4rem 0; text-transform:uppercase; letter-spacing:0.04em; font-size:0.6875rem; color:var(--webmodule-color-neutral-500,#64748b);"
            >
              ${LangPurchaseOrderDetailView.batchSectionRequested()}
            </h5>
            ${row(LangPurchaseOrderDetailView.batchDeliveryDate(), dateValue(b.deliveryDate))}
            ${row(LangPurchaseOrderDetailView.batchCustomerRef(), value(b.customerRef))}
            ${row(LangPurchaseOrderDetailView.batchStatus(), value(b.status))}
            ${row(LangPurchaseOrderDetailView.batchComment(), value(b.comment))}
          </section>
          <section class="po-batch-section" style="flex:1 1 16rem; min-width:14rem;">
            <h5
              style="margin:0 0 0.4rem 0; text-transform:uppercase; letter-spacing:0.04em; font-size:0.6875rem; color:var(--webmodule-color-neutral-500,#64748b);"
            >
              ${LangPurchaseOrderDetailView.batchSectionConfirmed()}
            </h5>
            ${confirmedBody}
          </section>
        </div>
      </div>
    `;
  }

  footerTemplate(): TemplateResult | null | undefined {
    return html`
      <webmodule-button size="small" variant="primary" @click=${() => this.hideModal()}>
        ${LangPurchaseOrderDetailView.close()}
      </webmodule-button>
    `;
  }
}
